import {openDeleteDialog, showNotification} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {
  useDeleteChecklistDefinitionMutation,
  useRemoveDefaultChecklistDefinitionMutation,
  useSetDefaultChecklistDefinitionMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function Checklist() {
  const navigate = useNavigate();

  const [deleteChecklistDefinition] = useDeleteChecklistDefinitionMutation();
  const [setDefaultChecklistDefinition] = useSetDefaultChecklistDefinitionMutation();
  const [removeDefaultChecklistDefinition] = useRemoveDefaultChecklistDefinitionMutation();

  const handleActionsCallback: ActionCallback = (action) => {
    const rowId = Array.isArray(action.rowId) ? action.rowId[0] : action.rowId;

    match(action.actionKey)
      .with('detail', 'edit', () =>
        navigate(composePath(settingsRoutes.generalChecklistDetail, {params: {id: rowId}}))
      )
      .with('setDefault', () => {
        setDefaultChecklistDefinition({definitionId: rowId})
          .unwrap()
          .then(() => {
            showNotification.success();
            action.refreshData();
          })
          .catch(handleApiError);
      })
      .with('unsetDefault', () => {
        removeDefaultChecklistDefinition({definitionId: rowId})
          .unwrap()
          .then(() => {
            showNotification.success();
            action.refreshData();
          })
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('general.actions.confirmNeutralQuestion'),
          onConfirm: () => {
            deleteChecklistDefinition({definitionId: rowId})
              .unwrap()
              .then(() => {
                action.refreshData();
              })
              .catch(handleApiError);
          },
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(action.actionKey));
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('page.settings.generalChecklist.title'),
        actions: [
          {
            type: 'button',
            title: i18n.t('page.settings.generalChecklist.actions.create'),
            to: settingsRoutes.generalChecklistCreate,
          },
        ],
      }}
      data-testid={testIds.settings.generalChecklist()}
    >
      <VStack spacing={6} height="100%">
        <Box maxWidth={200}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.generalChecklist.description')}
          </Text>
        </Box>
        <Box height="100%">
          <DmsDataGrid
            data-testid={testIds.settings.generalChecklist('grid')}
            gridCode="checklist-definition"
            actionCallback={handleActionsCallback}
            autoHeight
          />
        </Box>
      </VStack>
    </SettingsTemplate>
  );
}
